//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import Icon from '@components/atoms/icon/Icon';
import Text, { BodyText } from '@components/atoms/text/Text';
import {
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  TikTokIcon,
  TwitterIcon,
  YouTubeIcon,
} from '@components/molecules/SocialNetworkIcon';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { SocialNetwork } from '@models/SocialNetwork';
import { Mixins } from '@theme/mixins';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { isDefined } from '@utils/filterPredicates';

const ICON_SIZE = 16;

const networksConfig = [
  {
    icon: <PinterestIcon size={ICON_SIZE} />,
    label: 'View on Pinterest',
    network: SocialNetwork.Pinterest,
  },
  {
    icon: <InstagramIcon size={ICON_SIZE} />,
    label: 'View on Instagram',
    network: SocialNetwork.Instagram,
  },
  {
    label: 'View on Tumblr',
    network: SocialNetwork.Tumblr,
  },
  {
    icon: <FacebookIcon size={ICON_SIZE} />,
    label: 'View on Facebook',
    network: SocialNetwork.Facebook,
  },
  {
    icon: <TwitterIcon size={ICON_SIZE} />,
    label: 'View on Twitter',
    network: SocialNetwork.Twitter,
  },
  {
    icon: <TikTokIcon size={ICON_SIZE} />,
    label: 'View on TikTok',
    network: SocialNetwork.TikTok,
  },
  {
    icon: <YouTubeIcon size={ICON_SIZE} />,
    label: 'View on YouTube',
    network: SocialNetwork.YouTube,
  },
];

export interface UserWebLinkProps {
  helperText?: string;
  href?: string;
  isDisabled?: boolean;
  isProfile?: boolean;
  network: SocialNetwork;
}

export const UserWebLink = ({
  helperText,
  href,
  isDisabled,
  network,
}: UserWebLinkProps) => {
  const config = networksConfig.find((config) => config.network === network);

  if (!isDefined(config)) {
    return (
      <Label>
        <BodyText>Uploaded</BodyText>
        <Icon name='upload' />
      </Label>
    );
  }

  const label = (
    <Label>
      <Text
        mixin={isDisabled ? Mixins.Text.BodyDisabled : Mixins.Text.LinkPrimary}
      >
        {config.label}
      </Text>
      {config.icon && <>{config.icon}</>}
    </Label>
  );

  return isDisabled ? (
    <SmallTooltip
      content={helperText}
      isDisabled={!isDefined(helperText)}
      target={label}
    />
  ) : (
    <PrimaryAnchor
      href={href}
      rel='noreferrer'
      target='_blank'
      trackingId={UIATrackingIds.DETAILS_OPEN_USER_WEB_LINK}
    >
      {label}
    </PrimaryAnchor>
  );
};

const Label = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[2]}
  `,
);
